import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePlan } from "../context/PlanContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PromoBanner from "../components/PromoBanner";
import useLiveQuotes from "../hooks/useLiveQuotes";
import DataBadge from "../components/DataBadge";
import { Plus, Star, Trash2, Pencil, Check, X, Search, Grip, Zap } from "lucide-react";
import { toast } from "sonner";

const SYMBOL_SUGGESTIONS = [
  "AAPL", "MSFT", "NVDA", "TSLA", "AMD", "META", "AMZN", "GOOGL", "SPY", "QQQ",
  "PLTR", "COIN", "MARA", "DIA", "IWM", "XLK", "XLE", "SMH",
];

export default function Watchlists() {
  const { plan, limits, showUpgrade } = usePlan();
  const [watchlists, setWatchlists] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [symbolInput, setSymbolInput] = useState("");
  const [search, setSearch] = useState("");
  const live = useLiveQuotes();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/watchlists");
      setWatchlists(data.watchlists || []);
      if (!activeId && data.watchlists?.length) setActiveId(data.watchlists[0].id);
    } catch (e) {
      toast.error("Could not load watchlists");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const loadSymbols = async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/api/watchlists/${id}/symbols`);
      setSymbols(data.symbols || []);
    } catch (e) { setSymbols([]); }
  };
  useEffect(() => { loadSymbols(activeId); }, [activeId]);

  const active = watchlists.find((w) => w.id === activeId);

  const createWatchlist = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const { data } = await api.post("/api/watchlists", { name });
      setNewName("");
      await load();
      setActiveId(data.id);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 402) {
        showUpgrade((d && d.code) || "watchlists");
      } else {
        toast.error(formatApiErrorDetail(d) || "Could not create watchlist");
      }
    } finally { setCreating(false); }
  };

  const rename = async (id) => {
    const name = renameValue.trim();
    if (!name) { setRenamingId(null); return; }
    try {
      await api.patch(`/api/watchlists/${id}`, { name });
      toast.success("Renamed");
      setRenamingId(null);
      await load();
    } catch (err) { toast.error("Rename failed"); }
  };

  const toggleFavorite = async (wl) => {
    try {
      await api.patch(`/api/watchlists/${wl.id}`, { is_favorite: !wl.is_favorite });
      await load();
    } catch (err) { toast.error("Update failed"); }
  };

  const setDefault = async (wl) => {
    try {
      await api.patch(`/api/watchlists/${wl.id}`, { is_default: true });
      toast.success(`"${wl.name}" is now default`);
      await load();
    } catch (err) { toast.error("Update failed"); }
  };

  const deleteWatchlist = async (wl) => {
    if (!confirm(`Delete watchlist "${wl.name}"?`)) return;
    try {
      await api.delete(`/api/watchlists/${wl.id}`);
      toast.success("Deleted");
      if (activeId === wl.id) setActiveId(null);
      await load();
    } catch (err) { toast.error("Delete failed"); }
  };

  const addSymbol = async (ticker) => {
    const tk = (ticker || symbolInput).trim().toUpperCase();
    if (!tk || !activeId) return;
    try {
      await api.post(`/api/watchlists/${activeId}/symbols`, { ticker: tk });
      setSymbolInput("");
      await loadSymbols(activeId);
      await load();
      toast.success(`${tk} added`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 402) {
        showUpgrade((d && d.code) || "symbol_limit");
      } else {
        toast.error(formatApiErrorDetail(d) || "Add failed");
      }
    }
  };

  const removeSymbol = async (ticker) => {
    if (!activeId) return;
    try {
      await api.delete(`/api/watchlists/${activeId}/symbols/${ticker}`);
      await loadSymbols(activeId);
      await load();
    } catch (err) { toast.error("Remove failed"); }
  };

  const filteredSymbols = useMemo(() => {
    if (!search.trim()) return symbols;
    const q = search.toUpperCase();
    return symbols.filter((s) => s.ticker.includes(q));
  }, [symbols, search]);

  const capText = useMemo(() => {
    const wlCap = limits.watchlists == null ? "Unlimited" : limits.watchlists;
    const sCap = limits.symbols_per_watchlist == null ? "Unlimited"
               : limits.symbols_per_watchlist >= 1000 ? "Unlimited (1,000 soft cap)"
               : limits.symbols_per_watchlist;
    return `${wlCap} watchlist${wlCap === 1 ? "" : "s"} · ${sCap} symbol${sCap === 1 ? "" : "s"} each`;
  }, [limits]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="watchlists-page">
      <Header />
      <PromoBanner />
      <main id="main-content" className="flex-1 max-w-[1600px] w-full mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <div className="label-mono">Watchlists</div>
            <h1 className="text-3xl sm:text-4xl text-white font-semibold tracking-tight mt-1">Your Watchlists</h1>
            <div className="text-white/50 text-[12px] mt-1">
              Plan: <span className="text-white/85 uppercase font-mono">{plan}</span> · {capText}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createWatchlist()}
              placeholder="New watchlist name"
              className="input-glass w-56"
              data-testid="new-watchlist-input"
            />
            <button
              onClick={createWatchlist}
              disabled={creating || !newName.trim()}
              className="btn-accent inline-flex items-center gap-2"
              data-testid="create-watchlist-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Create
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
          {/* Left: watchlists list */}
          <aside className="card-surface p-3 self-start">
            {loading && <div className="text-white/50 text-xs p-3">Loading…</div>}
            {!loading && watchlists.length === 0 && (
              <div className="text-white/50 text-xs p-3">No watchlists yet.</div>
            )}
            <div className="space-y-1">
              {watchlists.map((wl) => (
                <div
                  key={wl.id}
                  onClick={() => setActiveId(wl.id)}
                  className={`group px-3 py-2.5 rounded-xl cursor-pointer transition-all border ${
                    activeId === wl.id
                      ? "bg-white/[0.06] border-white/20"
                      : "border-white/5 hover:border-white/15 hover:bg-white/[0.03]"
                  }`}
                  data-testid={`watchlist-item-${wl.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    {renamingId === wl.id ? (
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => rename(wl.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") rename(wl.id);
                          if (e.key === "Escape") setRenamingId(null);
                        }}
                        className="input-glass py-1 text-[13px] flex-1"
                        data-testid={`rename-input-${wl.id}`}
                      />
                    ) : (
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-[13px] font-semibold tracking-tight truncate flex items-center gap-1.5">
                          {wl.name}
                          {wl.is_default && (
                            <span className="text-[9px] font-mono uppercase tracking-widest text-[var(--bull)]">Default</span>
                          )}
                        </div>
                        <div className="text-[10px] label-mono mt-0.5">
                          {wl.symbol_count} symbol{wl.symbol_count === 1 ? "" : "s"}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-0.5 opacity-70 group-hover:opacity-100">
                      <button onClick={(e) => { e.stopPropagation(); toggleFavorite(wl); }}
                              className="p-1.5 text-white/45 hover:text-[#ffd166]" title="Favorite"
                              data-testid={`favorite-btn-${wl.id}`}>
                        <Star className="w-3.5 h-3.5" fill={wl.is_favorite ? "#ffd166" : "none"} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); setRenamingId(wl.id); setRenameValue(wl.name); }}
                              className="p-1.5 text-white/45 hover:text-white" title="Rename"
                              data-testid={`rename-btn-${wl.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); deleteWatchlist(wl); }}
                              className="p-1.5 text-white/45 hover:text-[var(--bear)]" title="Delete"
                              data-testid={`delete-btn-${wl.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {activeId === wl.id && !wl.is_default && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDefault(wl); }}
                      className="mt-2 text-[10px] label-mono text-white/45 hover:text-white"
                      data-testid={`set-default-btn-${wl.id}`}
                    >
                      Set as default
                    </button>
                  )}
                </div>
              ))}
            </div>
          </aside>

          {/* Right: active watchlist detail */}
          <section className="card-surface p-5">
            {!active && <div className="text-white/50 text-sm py-12 text-center">Select a watchlist</div>}
            {active && (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                  <div className="flex-1">
                    <h2 className="text-xl text-white font-semibold tracking-tight">{active.name}</h2>
                    <div className="label-mono mt-1">
                      {symbols.length} / {limits.symbols_per_watchlist == null ? "∞" : limits.symbols_per_watchlist} symbols
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                      <input value={search} onChange={(e) => setSearch(e.target.value)}
                             placeholder="Search symbols"
                             className="input-glass pl-8 w-44 text-[13px] py-1.5"
                             data-testid="watchlist-search"/>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <input
                    value={symbolInput}
                    onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && addSymbol()}
                    placeholder="Add symbol (e.g. AAPL)"
                    className="input-glass flex-1"
                    data-testid="add-symbol-input"
                  />
                  <button
                    onClick={() => addSymbol()}
                    className="btn-accent inline-flex items-center gap-2"
                    disabled={!symbolInput.trim()}
                    data-testid="add-symbol-btn"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4 label-mono">
                  Popular:
                  {SYMBOL_SUGGESTIONS.slice(0, 10).map((s) => (
                    <button key={s} onClick={() => addSymbol(s)}
                            className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-full border border-white/10 text-white/70 hover:border-white/30 hover:text-white transition-colors"
                            data-testid={`suggestion-${s}`}>
                      {s}
                    </button>
                  ))}
                </div>

                <div className="border border-white/5 rounded-2xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-white/[0.03] text-[10px] font-mono uppercase tracking-wider text-white/55">
                        <th className="text-left px-3 py-2.5">Ticker</th>
                        <th className="text-right px-3 py-2.5">Price</th>
                        <th className="text-right px-3 py-2.5">Change</th>
                        <th className="text-right px-3 py-2.5">Status</th>
                        <th className="text-right px-3 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSymbols.length === 0 && (
                        <tr><td colSpan={5} className="text-center text-white/40 py-8 text-xs">No symbols yet</td></tr>
                      )}
                      {filteredSymbols.map((s) => {
                        const q = (live.quotes || {})[s.ticker];
                        const price = q?.price;
                        const chg = q?.change_pct;
                        return (
                          <tr key={s.ticker} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`row-${s.ticker}`}>
                            <td className="px-3 py-2.5 text-white font-semibold">{s.ticker}</td>
                            <td className="px-3 py-2.5 text-right font-mono tabular-nums text-white/90">
                              {price != null ? `$${price.toFixed(2)}` : "—"}
                            </td>
                            <td className={`px-3 py-2.5 text-right font-mono tabular-nums ${
                              chg == null ? "text-white/40" : chg >= 0 ? "text-[var(--bull)]" : "text-[var(--bear)]"
                            }`}>
                              {chg == null ? "—" : `${chg >= 0 ? "+" : ""}${chg.toFixed(2)}%`}
                            </td>
                            <td className="px-3 py-2.5 text-right">
                              <DataBadge status={q?.status || "delayed"} lastEventAt={live.lastEventAt} liveCount={1} total={1} />
                            </td>
                            <td className="px-3 py-2.5 text-right">
                              <button onClick={() => removeSymbol(s.ticker)}
                                      className="text-white/40 hover:text-[var(--bear)] p-1"
                                      data-testid={`remove-symbol-${s.ticker}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
